import "./App.css";
import { useLayoutEffect, useState, useEffect, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useParams,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Header from "./header.js";
import Footer from "./footer.js";
import Pricing from "./pages/landing/pricing.js";
import JoinSection from "./pages/landing/join.js";
import WelcomeSection from "./pages/landing/section1.js";
import PreventLandscape from "./utils/preventlandscape.js";
import Inception from "./pages/landing/inception.js";
import { slideFade } from "./utils/animations.js";
import Reveal from "react-awesome-reveal";
import Flow1 from "./pages/landing/flow1.js";
import NotFound from "./pages/404.js";
import AuthOverlay from "./pages/auth/AuthOverlay.js";
import Terms from "./pages/terms.js";
import Privacy from "./pages/privacy.js";

function ScrollWrapper({ children }) {
  const location = useLocation();
  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);
  return children;
}

function Landing({ onAuthOpen }) {
  return (
    <>
      <WelcomeSection onAuthOpen={onAuthOpen} />
      <Flow1 />
      <Pricing onAuthOpen={onAuthOpen} />
      <Reveal
        keyframes={slideFade}
        duration={1500}
        triggerOnce={true}
        delay={100}
        fraction={0.3}
      >
        <Inception />
        <JoinSection onAuthOpen={onAuthOpen} />
      </Reveal>
    </>
  );
}

function MainApp() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

function AppShell() {
  const location = useLocation();
  const [showAuth, setShowAuth] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [isVerify, setIsVerify] = useState(false);
  const [resetToken, setResetToken] = useState(null);
  const [emailConfirm, setEmailConfirm] = useState(false);
  const [confirmUserId, setConfirmUserId] = useState(null);
  const [confirmToken, setConfirmToken] = useState(null);
  const { user } = useAuth();

  const isConfirmPath = location.pathname.startsWith("/confirm/");

  // If the user is already authenticated, boot them to the separate GCP app.
  // Skip when completing an email-change link (must run PATCH while possibly logged in).
  useEffect(() => {
    if (
      !isConfirmPath &&
      user &&
      (showAuth ||
        location.pathname === "/login" ||
        location.pathname === "/signup")
    ) {
      window.location.href = "/app";
    }
  }, [user, showAuth, isConfirmPath, location.pathname]);

  const openAuth = useCallback(
    (signup = false, verify = false, token = null) => {
      setShowAuth(true);
      setIsSignUp(signup);
      setIsVerify(verify);
      setResetToken(token);
      setEmailConfirm(false);
      setConfirmUserId(null);
      setConfirmToken(null);
    },
    [],
  );

  const openEmailConfirm = useCallback((id, token) => {
    setShowAuth(true);
    setIsSignUp(false);
    setIsVerify(false);
    setResetToken(null);
    setEmailConfirm(true);
    setConfirmUserId(id);
    setConfirmToken(token);
  }, []);

  const closeAuth = () => {
    setShowAuth(false);
    setResetToken(null);
    setEmailConfirm(false);
    setConfirmUserId(null);
    setConfirmToken(null);
    if (
      location.pathname === "/login" ||
      location.pathname === "/signup"
    ) {
      window.location.href = "/";
    }
  };

  return (
    <ScrollWrapper>
      <PreventLandscape minHeight={500}>
        <Header onAuthOpen={() => openAuth(false)}></Header>
        <Routes>
          <Route
            path="/"
            element={<Landing onAuthOpen={() => openAuth(true)} />}
          />

          <Route
            path="/login"
            element={
              <>
                <Landing onAuthOpen={() => openAuth(false)} />
                <AuthOverlayWrapper
                  open={true}
                  signup={false}
                  onClose={closeAuth}
                />
              </>
            }
          />

          <Route
            path="/signup"
            element={
              <>
                <Landing onAuthOpen={() => openAuth(true)} />
                <AuthOverlayWrapper
                  open={true}
                  signup={true}
                  onClose={closeAuth}
                />
              </>
            }
          />

          <Route
            path="/verify/:token"
            element={<VerifyPage openAuth={openAuth} />}
          />
          <Route
            path="/reset/:id/:token"
            element={<ResetPage openAuth={openAuth} />}
          />
          <Route
            path="/confirm/:id/:token"
            element={
              <ConfirmEmailPage
                openAuth={openAuth}
                openEmailConfirm={openEmailConfirm}
              />
            }
          />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Footer></Footer>

        <AuthOverlayWrapper
          open={showAuth}
          signup={isSignUp}
          verify={isVerify}
          resetToken={resetToken}
          emailConfirm={emailConfirm}
          confirmUserId={confirmUserId}
          confirmToken={confirmToken}
          onClose={closeAuth}
        />
      </PreventLandscape>
    </ScrollWrapper>
  );
}

function AuthOverlayWrapper(props) {
  return <AuthOverlay {...props} onResetClose={props.onClose} />;
}

function VerifyPage({ openAuth }) {
  const { token } = useParams();
  useLayoutEffect(() => {
    openAuth(false, true, token);
  }, [openAuth, token]);

  return <Landing onAuthOpen={() => openAuth(true)} />;
}

function ResetPage({ openAuth }) {
  const { id, token } = useParams();
  useLayoutEffect(() => {
    openAuth(false, false, `${id}/${token}`);
  }, [openAuth, id, token]);
  return <Landing onAuthOpen={() => openAuth(true)} />;
}

function ConfirmEmailPage({ openAuth, openEmailConfirm }) {
  const { id, token } = useParams();
  useLayoutEffect(() => {
    openEmailConfirm(id, token);
  }, [openEmailConfirm, id, token]);

  return <Landing onAuthOpen={() => openAuth(true)} />;
}

export default function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}
